import React from "react";
import { NavLink } from "react-router-dom";
import { Leaf, Mail, Phone, MapPin } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

export default function Footer() {
  const { t, lang } = useI18n();
  const base = `/${lang}`;

  return (
    <footer
      data-testid="site-footer"
      className="bg-slate-950 text-slate-300"
    >
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-10">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-emerald-600 text-white">
                <Leaf className="h-6 w-6" strokeWidth={2.25} />
              </span>
              <span className="font-heading text-base font-bold tracking-tight text-white">
                {t("brand.name")}
              </span>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-slate-400">
              {t("footer.about_text")}
            </p>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold uppercase tracking-widest text-emerald-400">
              {t("footer.nav_title")}
            </h4>
            <ul className="mt-5 space-y-3 text-sm">
              <li>
                <NavLink
                  to={base}
                  data-testid="footer-link-home"
                  className="hover:text-emerald-400"
                >
                  {t("header.nav.home")}
                </NavLink>
              </li>
              <li>
                <a
                  href={`${base}#sectors`}
                  data-testid="footer-link-sectors"
                  className="hover:text-emerald-400"
                >
                  {t("header.nav.sectors")}
                </a>
              </li>
              <li>
                <a
                  href={`${base}#circuit`}
                  data-testid="footer-link-analysis"
                  className="hover:text-emerald-400"
                >
                  {t("header.nav.analysis")}
                </a>
              </li>
              <li>
                <a
                  href={`${base}#newsletter`}
                  data-testid="footer-link-newsletter"
                  className="hover:text-emerald-400"
                >
                  {t("header.nav.newsletter")}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold uppercase tracking-widest text-emerald-400">
              {t("footer.legal_title")}
            </h4>
            <ul className="mt-5 space-y-3 text-sm">
              <li>
                <NavLink
                  to={`${base}/privacy`}
                  data-testid="footer-link-privacy"
                  className="hover:text-emerald-400"
                >
                  {t("pages.privacy.title")}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to={`${base}/cookies`}
                  data-testid="footer-link-cookies"
                  className="hover:text-emerald-400"
                >
                  {t("pages.cookies.title")}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to={`${base}/terms`}
                  data-testid="footer-link-terms"
                  className="hover:text-emerald-400"
                >
                  {t("pages.terms.title")}
                </NavLink>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold uppercase tracking-widest text-emerald-400">
              {t("footer.contact_title")}
            </h4>
            <ul className="mt-5 space-y-3 text-sm text-slate-300">
              <li className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 text-emerald-400" />
                <span>
                  {t("footer.address_line1")}
                  <br />
                  {t("footer.address_line2")}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="mt-0.5 h-4 w-4 text-emerald-400" />
                <a
                  href={`mailto:${t("footer.email_value")}`}
                  data-testid="footer-email"
                  className="hover:text-emerald-400"
                >
                  {t("footer.email_value")}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="mt-0.5 h-4 w-4 text-emerald-400" />
                <a
                  href={`tel:${t("footer.phone_value").replace(/\s/g, "")}`}
                  data-testid="footer-phone"
                  className="hover:text-emerald-400"
                >
                  {t("footer.phone_value")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 grid gap-8 border-t border-slate-800 pt-8 md:grid-cols-2">
          <div className="space-y-1 text-xs leading-relaxed text-slate-400">
            <p className="font-semibold text-slate-200">
              {t("footer.legal_details.company")}
            </p>
            <p>{t("footer.legal_details.registration")}</p>
            <p>{t("footer.legal_details.siret")}</p>
            <p>{t("footer.legal_details.vat")}</p>
            <p>{t("footer.legal_details.capital")}</p>
            <p>{t("footer.legal_details.founded")}</p>
            <p>{t("footer.legal_details.license")}</p>
            <p>{t("footer.legal_details.jurisdiction")}</p>
            <p>{t("footer.legal_details.director")}</p>
          </div>
          <div className="text-xs leading-relaxed text-slate-400">
            <p>{t("footer.disclaimer")}</p>
          </div>
        </div>

        <div className="mt-8 border-t border-slate-800 pt-6 text-xs text-slate-500">
          {t("footer.copyright")}
        </div>
      </div>
    </footer>
  );
}
